<template>
  <div class="panel">
    <h2>Запись трека</h2>
    <p class="recorder-status">
      Статус: {{ statusLabel }} · Точек: {{ points.length }}
    </p>
    <div class="btn-row">
      <button class="btn" :disabled="recording" @click="startRecording">Старт</button>
      <button class="btn btn-secondary" :disabled="!recording" @click="stopRecording">Стоп</button>
      <button class="btn" :disabled="points.length < 2 || saving" @click="saveTrack">
        {{ saving ? 'Сохранение...' : 'Сохранить' }}
      </button>
    </div>
    <p v-if="error" class="error-message">{{ error }}</p>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref } from 'vue'
import { createTrackFromPoints, updateTrack } from '../api/tracks'

const props = defineProps({
  metadata: {
    type: Object,
    required: true,
  },
})

const emit = defineEmits(['saved'])

const points = ref([])
const recording = ref(false)
const saving = ref(false)
const error = ref('')
let watchId = null

const statusLabel = computed(() => (recording.value ? 'идёт запись' : 'остановлена'))

function startRecording() {
  if (!navigator.geolocation) {
    error.value = 'Geolocation недоступен в этом браузере'
    return
  }

  error.value = ''
  recording.value = true

  watchId = navigator.geolocation.watchPosition(
    (position) => {
      points.value.push([
        position.coords.latitude,
        position.coords.longitude,
        new Date(position.timestamp).toISOString(),
      ])
    },
    (geoError) => {
      error.value = geoError.message
      stopRecording()
    },
    {
      enableHighAccuracy: true,
      maximumAge: 0,
      timeout: 10000,
    }
  )
}

function stopRecording() {
  recording.value = false
  if (watchId !== null) {
    navigator.geolocation.clearWatch(watchId)
    watchId = null
  }
}

async function saveTrack() {
  if (points.value.length < 2) return
  if (!props.metadata.name?.trim()) {
    error.value = 'Укажите название трека'
    return
  }

  saving.value = true
  error.value = ''

  try {
    const { data } = await createTrackFromPoints({
      name: props.metadata.name,
      description: props.metadata.description,
      difficulty_profiles: props.metadata.difficulty_profiles,
      public: props.metadata.public,
      source: 'recorded',
      points: points.value,
    })

    const pendingPhotos = props.metadata.pendingPhotos || []
    if (pendingPhotos.length) {
      const { data: updated } = await updateTrack(
        data.id,
        {},
        {
          photos: pendingPhotos.map((photo) => photo.file),
          photoMetadata: pendingPhotos.map((photo) =>
            photo.gps ? { lat: photo.gps.lat, lng: photo.gps.lng } : {}
          ),
        }
      )
      emit('saved', updated)
    } else {
      emit('saved', data)
    }
    points.value = []
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось сохранить трек'
  } finally {
    saving.value = false
  }
}

onBeforeUnmount(() => {
  stopRecording()
})
</script>
